import React from 'react';
import {Divider, Stack, Typography} from '@mui/material';
import ClassificationField from '../../../components/prosemirror-editor/components/documentHeader/ClassificationField';
import {formatHeaderDtgDisplay, formatHeaderFieldDisplay} from '../../../utils/documentHeaderExport';
import {useSession} from '../../../firebase/UserProvider';
import EntityTagsSection, {TAGS_SECTION_HEADING_SX} from '../../collection-requests/tags/EntityTagsSection';
import PreviewAuthorBadges from './PreviewAuthorBadges';
import {DEFAULT_HEADER_DATA} from '../../../components/prosemirror-editor/components/documentHeader/headerFieldOptions';

const SECTION_HEADING_SX = TAGS_SECTION_HEADING_SX;

function ReadOnlyField({label, value}) {
    const display = formatHeaderFieldDisplay(value);

    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Typography variant="body2" color="text.secondary" sx={{minWidth: '150px', flexShrink: 0}}>
                {label}:
            </Typography>
            <Typography variant="body1" sx={{flex: 1, minWidth: 0, lineHeight: 1.5}}>
                {display || '—'}
            </Typography>
        </Stack>
    );
}

/**
 * Read-only document header for preview — mirrors editor Security / Production / Assessment layout.
 */
export default function PreviewDocumentHeader({
    headerData,
    icrNumber,
    reportId,
    authors = [],
    selectedAuthorId,
    onAuthorClick,
}) {
    const {role, roles} = useSession();
    const canManageTags = role === 'collection_manager' || role === 'admin'
        || (roles || []).some((r) => r === 'collection_manager' || r === 'admin');
    const data = {...DEFAULT_HEADER_DATA, ...(headerData || {})};
    const dtgDisplay = formatHeaderDtgDisplay(data.dtg);

    return (
        <Stack
            spacing={2}
            p={2}
            bgcolor="#F9F9F9"
            borderRadius={1}
            mb={2}
            data-report-scroll-skip
        >
            {icrNumber && (
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                    ICR Number: {icrNumber}
                </Typography>
            )}

            <Stack spacing={1}>
                <Typography sx={SECTION_HEADING_SX}>Security</Typography>
                <Stack spacing={1}>
                    <ClassificationField value={data.classification}/>
                    <ReadOnlyField label="IFC" value={data.ifc}/>
                    <ReadOnlyField label="Distribution" value={data.distribution}/>
                    <ReadOnlyField label="Compartment" value={data.compartment}/>
                </Stack>
            </Stack>

            <Divider/>

            <Stack spacing={1}>
                <Typography sx={SECTION_HEADING_SX}>Production</Typography>
                <Stack spacing={1}>
                    <PreviewAuthorBadges
                        authors={authors}
                        selectedAuthorId={selectedAuthorId}
                        onAuthorClick={onAuthorClick}
                    />
                    <ReadOnlyField label="DTG" value={dtgDisplay}/>
                    <ReadOnlyField label="Originator" value={data.originator}/>
                    <ReadOnlyField label="Production Center" value={data.productionCenter}/>
                </Stack>
            </Stack>

            <Divider/>

            <Stack spacing={1}>
                <Typography sx={SECTION_HEADING_SX}>Assessment</Typography>
                <ReadOnlyField label="Confidence" value={data.confidence}/>
            </Stack>

            {reportId && (
                <>
                    <Divider/>
                    <Stack spacing={1}>
                        <Typography sx={SECTION_HEADING_SX}>Tags</Typography>
                        <EntityTagsSection
                            entityType="REPORT"
                            entityId={reportId}
                            canEdit={canManageTags}
                            enabled
                            dense
                            showHeading={false}
                        />
                    </Stack>
                </>
            )}
        </Stack>
    );
}
